export const RHI_QUERY_KEYS = {
  CREATE_LEAD: 'createLead',
  GET_RHI_LEADS: 'getRHILeads',
  UPDATE_LEAD: 'updateLead',
  GET_CONFIG: 'getConfig',
  RETAIL_HEALTH_INSURANCE_QUOTES: 'retail-health-insurance-quotes',
  /** Rider/plan-specific quote fetches (add-riders page). */
  QUOTES: 'quotes',
  GENERATE_QUOTE: 'generateQuote',
  GENERATE_PURCHASE_INTENT: 'generatePurchaseIntent',
  GET_MEDICAL_QUESTIONS: 'getMedicalQuestions',
  SUBMIT_MEDICAL_QUESTIONS: 'submitMedicalQuestions',
  CREATE_OTP: 'createOtp',
  VERIFY_OTP: 'verifyOtp',
  ADD_NOMINEE: 'addNominee',
  ADD_BANK_DETAILS: 'addBankDetails',
  GET_PROPOSAL: 'getProposal',
  GET_POLICY_STATUS: 'getPolicyStatus',
  PAYMENT_CALLBACK: 'paymentCallback',
} as const;

import { getUserScopedKey } from '@/utils/tokenUtils';

export const RHI_QUERY_NAMESPACE = 'rhi' as const;

/** Canonical TanStack Query key for generateQuote — rider/plan variants differ by the last segments. */
export const buildRhiGenerateQuoteKey = (
  leadId: string,
  sumInsured: number,
  essentialRidersIncluded: boolean,
  riders?: Array<string>,
  planIds?: Array<string>,
) =>
  [
    RHI_QUERY_NAMESPACE,
    RHI_QUERY_KEYS.GENERATE_QUOTE,
    leadId,
    sumInsured,
    essentialRidersIncluded,
    riders,
    planIds,
  ] as const;

/** TanStack Query key for rider-specific quotes (distinct from recommendation generateQuote). */
export const buildRhiQuotesKey = (
  leadId: string,
  sumInsured: number,
  essentialRidersIncluded: boolean,
  riders?: Array<string>,
  planIds?: Array<string>,
) =>
  [
    RHI_QUERY_NAMESPACE,
    RHI_QUERY_KEYS.QUOTES,
    leadId,
    sumInsured,
    essentialRidersIncluded,
    riders,
    planIds,
  ] as const;

export const buildRhiQueryKey = (
  key: (typeof RHI_QUERY_KEYS)[keyof typeof RHI_QUERY_KEYS],
  ...segments: unknown[]
) => [RHI_QUERY_NAMESPACE, key, ...segments] as const;

/** Ephemeral session keys — not in Zustand partialize; cleared explicitly by resetRhiFlow. */
export const RHI_EPHEMERAL_KEYS = {
  POST_PAYMENT_PENDING_START: 'rhi-post-payment-start',
  EKYC_RESEND_COUNT: 'rhi-ekyc-resend-count',
} as const;

/** User-scoped sessionStorage key for eKYC OTP resend count per quote. */
export const buildRhiEkycResendCountKey = (quoteId: string): string =>
  `${getUserScopedKey(RHI_EPHEMERAL_KEYS.EKYC_RESEND_COUNT)}-${quoteId}`;

/** BroadcastChannel name for payment status (not a sessionStorage key). */
export const RHI_BROADCAST_CHANNEL_NAME = 'rhi-payment-status';

export const RHI_TIMEOUT_VALUES = {
  PENDING_TIMEOUT_MS: 10 * 60 * 1000, // 10 minutes
  CONFIRMING_TIMEOUT_MS: 15 * 1000, // 15 seconds
  AWAITING_POLICY_TIMEOUT_MS: 2 * 60 * 1000, // 2 minutes
  SUCCESS_REDIRECT_TIMEOUT_MS: 12 * 1000, // 12 seconds
  POLICY_STATUS_POLLING_INTERVAL_MS: 5 * 1000, // 5 seconds
} as const;

export const RHI_CALLBACK_VALUES = {
  CALLBACK_RETRY_DELAY_MS: 5000, // 5 seconds
} as const;